/**
 * The print wrapper.
 *
 * Rendering a document and printing it are different problems, and only the
 * first has been solved. `renderA4()` and `renderThermal()` return a string;
 * something still has to hand that string to a printer, and that "something"
 * is where the 1.6.x bug would have survived if it had.
 *
 * THE ONE INVARIANT
 * -----------------
 *   `print()` is never called unless both document faces have loaded.
 *
 * Everything else here is mechanics. This is the part worth a test, because
 * failing it is silent: the invoice prints, it is emailed to a customer, and
 * its columns are a fraction of a millimetre out of line. There is no error and
 * no undo.
 *
 * WHY AN IFRAME
 * -------------
 * A new window would work, except that it cannot be measured before printing,
 * cannot have its fonts awaited, and popup blockers eat it. An iframe gives us
 * a real document whose `fonts` we can await and assert on - and the font faces
 * live on the IFRAME's document, not the parent's. Checking the parent's
 * `document.fonts` is the obvious mistake here, and it fails SILENTLY: the
 * parent has no DocSans `@font-face`, and `check()` reports an undeclared
 * family as ready. `assertDocFontsLoaded` requires a declared, loaded face, so
 * that mistake now fails loudly instead.
 */
import { assertDocFontsLoaded, type FontFaceSetLike } from '../../core/doc/font';
import { renderA4, type BusinessIdentity } from '../../core/doc/renderA4';
import { renderThermal, type RollWidth, type ThermalOptions } from '../../core/doc/thermal';
import type { NormalizedDocument } from '../../core/doc/types';

/**
 * The three things this module needs from a browser, injected so the ordering
 * invariant can be tested without one.
 */
export interface PrintEnvironment {
  /** The faces of the document being printed - the IFRAME's, not the parent's. */
  readonly fonts: FontFaceSetLike;
  /** Resolves when font loading has settled. */
  readonly ready: Promise<unknown>;
  /** Opens the print dialog. */
  readonly print: () => void;
}

/**
 * Await fonts, verify them, and only then print.
 *
 * The ORDER is the entire point, and it is asserted in the tests rather than
 * left to review:
 *   1. `ready` must settle first - checking before that reports a font as
 *      missing mid-load and refuses a perfectly good print.
 *   2. `assertDocFontsLoaded` must pass - otherwise we throw and `print` is
 *      never reached.
 *   3. `print` runs last, and only on success.
 */
export async function printSafely(env: PrintEnvironment): Promise<void> {
  await env.ready;
  assertDocFontsLoaded(env.fonts);
  env.print();
}

/** Overrides for `printHtml`, all optional - the defaults read the iframe. */
export type PrintHtmlOptions = Partial<PrintEnvironment>;

/**
 * The longest the application will stay frozen waiting for a dialog that never
 * announces itself.
 *
 * This was 60s, which is indefensible now that the wait has been measured. A
 * real print DOES fire `beforeprint`/`afterprint` on the top window (verified
 * against Chromium's own print pipeline), so on the browser target this timer
 * is not the normal path — it is the backstop for an engine that fires neither.
 * On that backstop, 60s is not "safe", it is the bug: the frame stays on screen
 * covering the app, Print stays `disabled`, and `activeElement` sits on `<body>`.
 * The user cannot do anything at all, and refreshing is the only escape.
 *
 * 5s is chosen because the fallback only runs when NO lifecycle event arrives.
 * Once a dialog has actually opened, the print has already been handed to the
 * print pipeline, so the expensive work is done and a user who has walked away
 * mid-dialog should not hold the app hostage for a minute.
 */
const FALLBACK_MS = 5_000;

/**
 * Wait for the print dialog to close, so the frame is not torn down under it.
 *
 * `window.print()` is NOT synchronous with respect to printing. It returns as
 * soon as the dialog is *requested*, while Chromium still has to lay out and
 * snapshot the frame's document. Removing the iframe in the same tick — which
 * is what `finally { frame.remove() }` did — deletes that document first, and
 * the print comes out blank.
 *
 * Nothing throws in that case, so the UI reported success. The user saw
 * "Sent to printer" and got no dialog and no page.
 *
 * WHY IT LISTENS ON THE TOP WINDOW — the actual cause of the freeze this fixes.
 *
 * `afterprint` is dispatched on the window that owns the printing context, and
 * engines disagree about which window that is once a print happens inside an
 * IFRAME. Measured in Chromium: calling `frameWindow.print()` fires `afterprint`
 * on the TOP window, and not on the frame. An earlier version listened only on
 * `frameWindow`, so on the browser target that listener NEVER ran. The wait then
 * fell through to its 60s timeout, and because the frame teardown and the focus
 * restoration both live in the `finally` AFTER this promise, the application sat
 * frozen for a full minute: frame still on screen, Print still `disabled`,
 * `activeElement` parked on `<body>`. That is the freeze the first fix missed.
 *
 * So both windows are listened to, because either may be the one that fires. The
 * timer remains as a bound for engines that fire neither.
 *
 * Returns an `arm` function rather than listening unconditionally. The top window
 * fires `afterprint` for ANY print, including one the user starts with Ctrl+P,
 * which has nothing to do with this invoice. Arming after the frame has actually
 * been asked to print is what stops an unrelated dialog from tearing this frame
 * down and stealing focus back.
 */
function whenPrintSettled(win: Window, timeoutMs = FALLBACK_MS): {
  settled: Promise<void>;
  arm: () => void;
} {
  let settledFlag = false;
  let armed = false;
  let resolveSettled: () => void = () => {};

  const settled = new Promise<void>((r) => {
    resolveSettled = r;
  });

  const finish = (): void => {
    if (settledFlag) return;
    settledFlag = true;
    win.removeEventListener('afterprint', finish);
    // Detach from the top window too, or every print leaves a listener behind.
    globalThis.removeEventListener?.('afterprint', onTopAfterPrint);
    // And cancel the fallback. It is idempotent, so leaving it queued is merely
    // wasteful rather than wrong — but a reprint that settled in 50ms would
    // otherwise hold a 5s timer, and a shop working through a stack of invoices
    // accumulates them.
    clearTimeout(fallback);
    resolveSettled();
  };

  function onTopAfterPrint(): void {
    // An `afterprint` arriving BEFORE this frame was printed belongs to a
    // different dialog. Ignoring it is the entire point of arming.
    if (armed) finish();
  }

  win.addEventListener('afterprint', finish, { once: true });
  globalThis.addEventListener?.('afterprint', onTopAfterPrint, { once: true });
  const fallback = setTimeout(finish, timeoutMs);

  return { settled, arm: () => { armed = true; } };
}

/**
 * Hand focus back to the control the user actually pressed.
 *
 * WHY THIS HAS TO EXIST — printing destroys focus in TWO independent places,
 * and neither one throws, so neither is visible from the outside:
 *
 *   1. `disabled`. Every caller wraps a print in a `busy` flag that puts
 *      `disabled` on the button which was just clicked. A disabled element
 *      cannot hold focus, so the browser drops it onto `<body>` the instant that
 *      state lands — before the dialog has even opened.
 *   2. the frame. `frameWindow.print()` moves focus into the frame's own realm,
 *      and `frame.remove()` then deletes the focused element along with it.
 *
 * The user sees a document that printed perfectly and an application that no
 * longer responds to the keyboard, which is indistinguishable from a hung app.
 * Refreshing "fixes" it, which is why this survived as long as it did.
 *
 * THE ORDERING TRAP — and the reason an earlier attempt at this does nothing.
 * The button is STILL disabled at the moment `printHtml` resolves: callers clear
 * their `busy` flag in a `finally` that runs *after* this promise settles.
 * Calling `focus()` right here therefore targets a disabled element, and the
 * browser discards it silently — the same symptom as doing nothing at all. So
 * this waits a frame at a time for the control to become focusable again, with a
 * hard bound so a screen that never re-enables its button cannot spin forever.
 */
function returnFocusAfterPrint(
  host: Document,
  frame: HTMLIFrameElement,
  trigger: HTMLElement | null,
): void {
  if (!trigger) return;
  let finished = false;
  let observer: MutationObserver | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;
  const stopRestoringOnUserInput = (): void => finish();

  const finish = (): void => {
    if (finished) return;
    finished = true;
    observer?.disconnect();
    host.removeEventListener?.('pointerdown', stopRestoringOnUserInput, true);
    host.removeEventListener?.('keydown', stopRestoringOnUserInput, true);
    if (retry !== undefined) clearTimeout(retry);
    clearTimeout(giveUp);
  };

  const scheduleRetry = (delay: number): void => {
    if (retry !== undefined) clearTimeout(retry);
    retry = setTimeout(attemptRestore, delay);
  };

  const attemptRestore = (): void => {
    if (finished) return;
    if (trigger.isConnected === false) {
      finish();
      return;
    }
    if ((trigger as HTMLButtonElement).disabled) {
      scheduleRetry(16);
      return;
    }

    const active = host.activeElement;
    const userMovedOn =
      active !== null &&
      active !== host.body &&
      active !== host.documentElement &&
      active !== trigger &&
      active !== frame &&
      !frame.contains(active);
    if (userMovedOn) {
      finish();
      return;
    }

    // The native dialog owns OS focus separately from the page's active element.
    // Request the page window first, then restore the element and verify both.
    host.defaultView?.focus();
    if (host.activeElement !== trigger) trigger.focus();

    if (
      host.activeElement === trigger &&
      (typeof host.hasFocus !== 'function' || host.hasFocus())
    ) {
      finish();
      return;
    }

    scheduleRetry(16);
  };

  if (
    typeof MutationObserver === 'function' &&
    'nodeType' in trigger &&
    trigger.nodeType === 1
  ) {
    observer = new MutationObserver(() => attemptRestore());
    observer.observe(trigger, { attributes: true, attributeFilter: ['disabled'] });
  }

  // A user click may not move focus in every browser/platform combination.
  // Stop the retry before it can reclaim focus during that interaction.
  host.addEventListener?.('pointerdown', stopRestoringOnUserInput, true);
  host.addEventListener?.('keydown', stopRestoringOnUserInput, true);

  // A bounded poll backs up the disabled-attribute observer and retries focus
  // when the browser has not yet returned focus to the page.
  const giveUp = setTimeout(finish, 10_000);
  scheduleRetry(150);
}

/** Render to a detached iframe and print it. */
export async function printHtml(
  html: string,
  host: Document,
  options: PrintHtmlOptions = {},
): Promise<void> {
  // Captured BEFORE the frame exists: `print()` moves focus into the frame's own
  // realm, and removing the frame deletes whatever had it. By the end of this
  // function there is nothing left to ask, so the question has to be asked here.
  //
  // Duck-typed rather than `instanceof HTMLElement`: an element that came from a
  // different realm (an iframe, a test double) fails an `instanceof` check while
  // being perfectly focusable, and silently yielding `null` here would reinstate
  // exactly the bug this is meant to fix.
  const activeAtStart = host.activeElement as (Element & { focus?: unknown }) | null;
  const trigger =
    activeAtStart && typeof activeAtStart.focus === 'function'
      ? (activeAtStart as HTMLElement)
      : null;

  const frame = host.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  /*
   * The frame must be INSIDE the viewport, and that is the second half of why
   * Print did nothing.
   *
   * This used to sit at `left: -10000px`, on the reasoning that a hidden or
   * zero-size iframe might skip layout and font loading. That reasoning was
   * half right and the conclusion was fatal: Chromium will not print content
   * that is laid out outside the viewport, and `print()` on such a frame is
   * routinely a silent no-op - no dialog, no page, no error. Combined with the
   * teardown race, Print was broken twice over and neither failure threw.
   *
   * So the frame is placed at the origin at true A4 size, opaque and above the
   * app. It is a white sheet covering the screen for as long as the dialog is
   * open, which is invisible in practice because the print dialog is modal and
   * sits above it - and it is removed the moment the dialog closes.
   *
   * `visibility` and `opacity` are left alone deliberately. Both are commonly
   * suggested as "invisible" alternatives to off-screen positioning, and both
   * reintroduce exactly the same problem: Chromium skips printing content it
   * considers unpainted. Size and position are the only safe variables.
   */
  Object.assign(frame.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '210mm',
    height: '297mm',
    border: '0',
    background: '#fff',
    pointerEvents: 'none',
    zIndex: '2147483647',
  });

  const doc = host.createElement('div');
  doc.textContent = '';
  frame.srcdoc = html;
  host.body.appendChild(frame);

  try {
    const frameWindow = frame.contentWindow;
    if (!frameWindow) throw new Error('The print frame produced no window.');
    const frameDoc = frameWindow.document;

    // srcdoc parsing is async; `load` is the only reliable signal that the
    // document exists and its stylesheet has been applied.
    await new Promise<void>((resolve, reject) => {
      frame.addEventListener('load', () => resolve(), { once: true });
      frame.addEventListener('error', () => reject(new Error('The print frame failed to load.')), {
        once: true,
      });
    });

    // `options.print` is a TEST SEAM: when it is supplied there is no browser
    // dialog, so waiting for `afterprint` would stall for the full fallback on
    // every injected print. Only a real `window.print()` opens something to wait
    // on.
    const usesRealDialog = options.print === undefined;

    // The frame MUST outlive the dialog. Removing it here instead is what made
    // Print silently produce nothing while still reporting success.
    if (usesRealDialog) {
      const wait = whenPrintSettled(frameWindow);
      // `arm()` after the print request has been made, so a Ctrl+P from the user
      // mid-print cannot be mistaken for this invoice's dialog closing.
      await printSafely({
        fonts: options.fonts ?? frameDoc.fonts,
        ready: options.ready ?? frameDoc.fonts.ready,
        print: () => {
          wait.arm();
          (options.print ?? (() => frameWindow.print()))();
        },
      });

      await wait.settled;

      // Do not start the focus retry loop while Chromium's native print preview
      // still owns focus. The loop watches React's post-print `busy` cleanup, so
      // arming it before the print lifecycle settles can race the dialog, the
      // frame and the caller's state update in the real browser.
      returnFocusAfterPrint(host, frame, trigger);
    } else {
      const print = options.print;
      if (print === undefined) throw new Error('The test print callback is required.');
      await printSafely({
        fonts: options.fonts ?? frameDoc.fonts,
        ready: options.ready ?? frameDoc.fonts.ready,
        print,
      });

      // For non-real dialogs (tests), also restore focus immediately.
      returnFocusAfterPrint(host, frame, trigger);
    }
  } finally {
    frame.remove();
  }
}

/** Print one document on A4. */
export async function printA4(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  host: Document,
  assetsBaseUrl = '',
): Promise<void> {
  return printHtml(renderA4(m, biz, {}, assetsBaseUrl), host, {});
}

/** Print one document on a thermal roll. */
export async function printThermal(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  host: Document,
  opt: { mediaWidthMm: RollWidth; printableWidthMm?: number; assetsBaseUrl?: string; copies?: number } & Omit<
    ThermalOptions,
    'mediaWidthMm' | 'printableWidthMm' | 'assetsBaseUrl' | 'copies'
  >,
): Promise<void> {
  return printHtml(
    renderThermal(m, biz, {
      mediaWidthMm: opt.mediaWidthMm,
      printableWidthMm: opt.printableWidthMm,
      assetsBaseUrl: opt.assetsBaseUrl,
      copies: opt.copies,
    }),
    host,
    {},
  );
}
